import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { ACCOUNT_ENTRY_TYPES, CUSTOMER_TAX_CONDITIONS, PAYMENT_METHODS, balanceLabel, type PaymentMethod } from '@pos/core';
import { patchRow, type AccountEntryRow } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { requestSync } from '../../services/sync.ts';
import { Alert, Avatar, Badge, Button, Card, Input, PageHeader, Select, Spinner, Stat, cx, errorMessage } from '../../ui/index.tsx';
import { AlertIcon, CashIcon, UsersIcon } from '../../ui/icons.tsx';
import { useMoney } from '../products/data.ts';
import { AccountEntryDialog, CustomerFormDialog } from './CustomerDialogs.tsx';
import { fetchStatement, filterCustomers, useBalances, useCustomers } from './data.ts';

type Filter = 'all' | 'debt';

/** Clientes: buscar, crear y ver saldos de cuenta corriente. */
export function CustomersPage() {
  const { can } = useSession();
  const customers = useCustomers();
  const balances = useBalances();
  const money = useMoney();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [creating, setCreating] = useState(false);
  const showBalance = can('customer_accounts');

  const list = useMemo(() => {
    let rows = filterCustomers(customers ?? [], query);
    if (filter === 'debt') rows = rows.filter((c) => (balances?.get(c.id) ?? 0) > 0);
    return rows;
  }, [customers, query, filter, balances]);
  const totalDebt = useMemo(() => [...(balances?.values() ?? [])].reduce((a, b) => a + Math.max(0, b), 0), [balances]);

  if (!customers) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        <Spinner />
      </div>
    );
  }

  const debtors = customers.filter((c) => (balances?.get(c.id) ?? 0) > 0).length;

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <PageHeader
        title="Clientes"
        description={showBalance ? 'Clientes y cuenta corriente (fiado). El saldo es la suma de compras a cuenta menos pagos.' : 'Clientes del negocio.'}
        actions={can('manage_customers') && <Button onClick={() => setCreating(true)}>Nuevo cliente</Button>}
      />
      {showBalance && (
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label="Te deben en total" icon={CashIcon} tone="amber" value={money(totalDebt)} />
          <Stat label="Clientes con deuda" icon={AlertIcon} tone={debtors ? 'amber' : 'slate'} value={String(debtors)} />
          <Stat label="Clientes" icon={UsersIcon} value={String(customers.length)} />
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
        <Input type="search" placeholder="Buscar por nombre, teléfono o documento…" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Buscar cliente" />
        {showBalance && (
          <Select value={filter} onChange={(e) => setFilter(e.target.value as Filter)} aria-label="Filtro">
            <option value="all">Todos</option>
            <option value="debt">Con deuda</option>
          </Select>
        )}
      </div>
      <Card flush className="divide-y divide-slate-100">
        {list.length === 0 && <p className="p-6 text-center text-sm text-slate-500">{customers.length ? 'Ningún cliente coincide.' : 'Todavía no hay clientes.'}</p>}
        {list.map((c) => {
          const b = balances?.get(c.id) ?? 0;
          return (
            <button key={c.id} type="button" onClick={() => navigate(`/clientes/${c.id}`)} className="flex w-full items-center gap-3 p-3 text-left hover:bg-slate-50 sm:px-4">
              <Avatar name={c.name} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">
                  {c.name} {!c.active && <Badge tone="slate">Inactivo</Badge>}
                </p>
                <p className="truncate text-sm text-slate-500">{[c.phone, c.doc_type && `${c.doc_type} ${c.doc_number}`].filter(Boolean).join(' · ') || 'Sin datos de contacto'}</p>
              </div>
              {showBalance && (
                <span
                  className={cx(
                    'shrink-0 rounded-full px-2.5 py-0.5 text-sm font-medium tabular-nums ring-1 ring-inset',
                    b > 0 ? 'bg-amber-50 text-amber-800 ring-amber-200' : b < 0 ? 'bg-emerald-50 text-emerald-800 ring-emerald-200' : 'bg-slate-50 text-slate-500 ring-slate-200',
                  )}
                >
                  {balanceLabel(b, money)}
                </span>
              )}
            </button>
          );
        })}
      </Card>
      <CustomerFormDialog open={creating} customer={null} onClose={() => setCreating(false)} onSaved={(id) => navigate(`/clientes/${id}`)} />
    </div>
  );
}

const dateFormat = new Intl.DateTimeFormat('es-AR', { dateStyle: 'short', timeStyle: 'short' });

/** Ficha del cliente: datos, saldo, movimientos de cuenta corriente, pagos y ajustes. */
export function CustomerDetailPage() {
  const { id } = useParams();
  const { can, isOwner, writer } = useSession();
  const money = useMoney();
  const customer = useLiveQuery(() => (id ? db.customers.get(id) : undefined), [id]);
  const balances = useBalances();
  const localEntries = useLiveQuery(() => (id ? db.account_entries.where('customer_id').equals(id).toArray() : []), [id]);
  const [statement, setStatement] = useState<{ entries: AccountEntryRow[]; online: boolean } | null>(null);
  const [editing, setEditing] = useState(false);
  const [entryMode, setEntryMode] = useState<'payment' | 'adjustment' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const showAccount = can('customer_accounts');

  // Se vuelve a pedir el estado de cuenta cuando cambian los movimientos locales (p. ej. un pago nuevo).
  useEffect(() => {
    if (!id || !showAccount) return;
    let cancelled = false;
    void fetchStatement(id).then((s) => !cancelled && setStatement(s));
    return () => {
      cancelled = true;
    };
  }, [id, showAccount, localEntries?.length]);

  if (customer === undefined) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        <Spinner />
      </div>
    );
  }
  if (!customer || customer.deleted_at) {
    return (
      <Card className="mx-auto max-w-lg text-center">
        <p className="font-medium">No encontramos ese cliente.</p>
        <Link to="/clientes" className="mt-3 inline-block text-sm font-medium text-brand">
          Volver a clientes
        </Link>
      </Card>
    );
  }

  const balance = balances?.get(customer.id) ?? 0;
  const toggleActive = async () => {
    setError(null);
    try {
      await patchRow(db, 'customers', customer.id, { active: !customer.active }, writer);
      requestSync(0);
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <PageHeader
        title={customer.name}
        description={[customer.phone, customer.doc_type && `${customer.doc_type} ${customer.doc_number}`, CUSTOMER_TAX_CONDITIONS[customer.tax_condition]].filter(Boolean).join(' · ')}
        actions={
          <>
            <Link to="/clientes">
              <Button variant="secondary">Volver</Button>
            </Link>
            {can('manage_customers') && (
              <Button variant="secondary" onClick={() => setEditing(true)}>
                Editar
              </Button>
            )}
          </>
        }
      />
      {(customer.address || customer.email || customer.notes) && (
        <Card className="space-y-1 text-sm text-slate-600">
          {customer.address && <p>Dirección: {customer.address}</p>}
          {customer.email && <p>Email: {customer.email}</p>}
          {customer.notes && <p>Notas: {customer.notes}</p>}
        </Card>
      )}

      {showAccount && (
        <>
          <Card className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm text-slate-500">Cuenta corriente</p>
              <p className={cx('text-2xl font-semibold tabular-nums', balance > 0 && 'text-amber-700')}>{balanceLabel(balance, money)}</p>
              <p className="text-xs text-slate-500">{customer.credit_limit != null ? `Límite ${money(customer.credit_limit)}` : 'Sin límite de crédito'}</p>
            </div>
            <div className="flex gap-2">
              {isOwner && (
                <Button variant="secondary" onClick={() => setEntryMode('adjustment')}>
                  Ajuste
                </Button>
              )}
              <Button onClick={() => setEntryMode('payment')}>Registrar pago</Button>
            </div>
          </Card>

          <Card flush className="divide-y divide-slate-100 text-sm">
            <h2 className="px-4 py-2 font-semibold">Movimientos</h2>
            {!statement && (
              <div className="flex justify-center p-4 text-slate-500">
                <Spinner />
              </div>
            )}
            {statement && !statement.online && <p className="px-4 py-2 text-amber-700">Sin conexión: se ven solo los movimientos hechos en este dispositivo.</p>}
            {statement && statement.entries.length === 0 && <p className="p-4 text-center text-slate-500">Sin movimientos.</p>}
            {statement?.entries.map((e) => (
              <div key={e.id} className="flex justify-between gap-3 px-4 py-2">
                <span className="min-w-0">
                  <span className="font-medium">{ACCOUNT_ENTRY_TYPES[e.type]}</span>
                  {e.method && <span className="text-slate-500"> · {PAYMENT_METHODS[e.method as PaymentMethod]?.label}</span>}
                  {e.note && <span className="text-slate-500"> · {e.note}</span>}
                  <span className="block text-xs text-slate-500">
                    {dateFormat.format(new Date(e.occurred_at))}
                    {e.server_xid === undefined && ' · sin enviar'}
                  </span>
                </span>
                <span className={cx('shrink-0 tabular-nums', e.amount > 0 ? 'text-amber-700' : 'text-emerald-700')}>
                  {e.amount > 0 ? '+' : '−'}
                  {money(Math.abs(e.amount))}
                </span>
              </div>
            ))}
          </Card>
        </>
      )}

      {can('manage_customers') && (
        <div className="flex justify-end">
          <Button variant="ghost" onClick={toggleActive}>
            {customer.active ? 'Desactivar cliente' : 'Reactivar cliente'}
          </Button>
        </div>
      )}
      {error && <Alert tone="error">{error}</Alert>}

      <CustomerFormDialog open={editing} customer={customer} onClose={() => setEditing(false)} />
      <AccountEntryDialog customer={customer} mode={entryMode} balance={balance} onClose={() => setEntryMode(null)} />
    </div>
  );
}
